import React, {useState, useEffect} from "react";
import {Skeleton, Box, Typography, Grid} from "@mui/material";
import DateRangePickerComponent from "../../common/CustomDateRangePicker";
import moment from "moment";
import useDashboard from "../../../hooks/useDashboard";
import EmptyIcon from "../../../assets/images/empty-box.png";
import useDashboardResetFilterStore from "../../../zustand/useDashboardResetFilterStore";

const AnomalyDetection = (props) => {
    const {breakpoints} = props;

    const {isDashboardResetFilters, setDashboardResetFilters} = useDashboardResetFilterStore();
    const {topProductAnomalyDetection, isFetchingAnomalyDetection, getProductAnomalyDetection} = useDashboard();

    const [startDate, setStartDate] = useState(moment().subtract(1, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [topAnomalyDetectionData, setTopAnomalyDetectionData] = useState([]);

    const [isAPIRefreshed, setAPIRefreshed] = useState(false);

    useEffect(() => {
        if (isDashboardResetFilters || isAPIRefreshed) {
            setStartDate(moment().subtract(1, 'days').format('YYYY-MM-DD'));
            setEndDate(moment().format("YYYY-MM-DD"));
        }
    }, [isDashboardResetFilters, isAPIRefreshed]);

    useEffect(() => {
        if (startDate && endDate) { 
            getProductAnomalyDetection(startDate, endDate);
            setDashboardResetFilters(false);
        }
    }, [startDate, endDate])

    useEffect(() => {
        if (startDate && endDate) {
            const intervalId = setInterval(() => {
                let initialStartDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
                let initialEndDate = moment().format('YYYY-MM-DD');

                getProductAnomalyDetection(initialStartDate, initialEndDate);
                setDashboardResetFilters(false);
                setAPIRefreshed(true);
            }, 8000);

            return () => clearInterval(intervalId);

            // if no auto refresh
            // let initialStartDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
            // let initialEndDate = moment().format('YYYY-MM-DD');

            // getProductAnomalyDetection(initialStartDate, initialEndDate);
            // setDashboardResetFilters(false);
        }
    },  [startDate, endDate])

    useEffect(() => {
        if (topProductAnomalyDetection) {
            const transformData = topProductAnomalyDetection?.map(item => ({
                count: item?.count,
                fraud_type: item?.fraud_type,
                product_code: item?.product_code || "Unknown",
                product_name: item?.productInfo?.product_name || "Unknown"
            }));

            setTopAnomalyDetectionData(transformData);
        }
    }, [topProductAnomalyDetection])


    const handleDateRangeChange = (startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate);
        setAPIRefreshed(false);
    }


    return (
        <Box sx={{backgroundColor: "#fff", mt: "14px", padding: "24px", pt: "28px", borderRadius: "8px", width: "95%", ml: 3, height: "22%"}}> 
            <Grid container>
                <Grid item xs={4.1} sm={4.1} md={4.1} lg={4.1} xl={4.1}>
                    <Typography sx={{color: "#58565f", fontSize: 16, mb: 3, fontWeight: 600}}>
                        Anomaly Detection
                    </Typography>
                </Grid>
        
                <Grid item xs={7.9} sm={7.9} md={7.9} lg={7.9} xl={7.9} sx={{textAlign: "end", justifyContent: "end", ml: "auto", display: "flex"}}>   
                    <DateRangePickerComponent 
                        initialStartDate={startDate}
                        initialEndDate={endDate}
                        ranges={{
                            "Last 24 Hours": [moment().subtract(1, 'day'), moment()],
                            "Last 7 Days": [moment().subtract(6, 'days'), moment()],
                            "This Month": [moment().startOf('month'), moment().endOf('month')],
                            "Last Month": [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
                            "Last 90 Days": [moment().subtract(89, 'days'), moment()],
                        }}
                        onDateRangeChange={handleDateRangeChange}
                    />
                </Grid>
            </Grid>
        
            <Box sx={{display: "block", mt: "-3px", height: "230px", overflowY: "auto",
                "&::-webkit-scrollbar": {
                    width: "8px"
                },
                "&::-webkit-scrollbar-track": {
                    background: "rgba(217, 217, 217, 0.5)"
                },
                "&::-webkit-scrollbar-thumb": {
                    background: "#7a78f7a6",
                    borderRadius: "4px"
                },
                "&::-webkit-scrollbar-thumb:hover": {
                    background: "#7A78F7"
                }}}
            >
                {isFetchingAnomalyDetection ?
                    <Box sx={{display: "block"}}>
                        <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                        <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                        <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                        <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                        <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                        <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                        <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                        <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                        <Skeleton variant="text" sx={{backgroundColor: "#d8d8fdbd"}} />
                    </Box>
                :
                    (!isFetchingAnomalyDetection && topAnomalyDetectionData?.length === 0) ?
                        <Box sx={{textAlign: "center", flexDirection: "column", 
                            justifyContent: "center", display: "flex", alignItems: "center", 
                        }}>
                            <img alt="empty-data-icon" src={EmptyIcon} width="90" style={{marginTop: "48px"}} />

                            <Typography sx={{color: "#58565f", fontSize: 14, mt: 1}}>
                                No data available for the selected date range
                            </Typography>
                        </Box>
                :
                    <ul style={{color: "#58565F"}}>
                        {topAnomalyDetectionData?.map((item, index) => (
                            <li key={index}>
                                {(item?.fraud_type === "Repeated Verification Attempts") ?   
                                    <Typography>
                                        {item?.product_name} has&nbsp; 

                                        <span style={{color: "rgb(249 40 8)", fontWeight: "bold"}}>{item?.count?.toLocaleString()}</span>

                                        &nbsp;{(item?.count > 1) ? "repeated verification attempts" : "repeated verification attempt"}
                                    </Typography>
                                :
                                    (item?.fraud_type === "Expired") ?
                                        <Typography>
                                            <span style={{color: "rgb(249 40 8)", fontWeight: "bold"}}>{item?.count?.toLocaleString()}</span>

                                            &nbsp;{item?.product_name} {(item?.count > 1) ? "have" : "has"} expired
                                        </Typography> 
                                :
                                    (item?.fraud_type === "Geographic Disparity") ?
                                        <Typography>
                                            {item?.product_name} has&nbsp; 

                                            <span style={{color: "rgb(249 40 8)", fontWeight: "bold"}}>{item?.count?.toLocaleString()}</span> 

                                            &nbsp;{(item?.count > 1) ? "geographic disparities" : "geographic disparity"} identified
                                        </Typography>
                                :
                                    (item?.fraud_type === "High Verification Frequency") ?
                                        <Typography>
                                            <span style={{color: "rgb(249 40 8)", fontWeight: "bold"}}>{item?.count?.toLocaleString()}</span>

                                            &nbsp;{item?.product_name} {(item?.count > 1) ? "have high verification frequencies" : "has high verification frequency"}
                                        </Typography> 
                                :
                                    (item?.fraud_type === "Suspicious Behavioral Pattern") ?
                                        <Typography>
                                            {item?.product_name} has&nbsp; 
                                            <span style={{color: "rgb(249 40 8)", fontWeight: "bold"}}>{item?.count?.toLocaleString()}</span>

                                            &nbsp;{(item?.count > 1) ? "suspicious behavioral patterns" : "suspicious behavioral pattern"}
                                        </Typography> 
                                :
                                    null
                                }
                            </li>
                        ))}
                    </ul>
                }
            </Box>
        </Box> 
    )
}

export default AnomalyDetection